// src/features/crypto-info/components/BreakoutAlertWidget.tsx
//
// Component Island: renders the server-computed quantitative swing-trading
// breakout setup (BULLISH BREAKOUT / EARLY UPTREND / BEARISH BREAKDOWN /
// OVERBOUGHT / WAIT). The 1:2 risk/reward price-guidance band (entry / take
// profit / stop / expected return) is only shown for actionable long setups
// (BUY / ACCUMULATE). Indicator readouts (RSI-14 / EMA-20 / SMA-50) are always
// shown as the analytical footer.

import type { BreakoutAlert } from '../api';

const fmtUsd = (val?: number): string =>
    val != null
        ? val.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          })
        : 'N/A';

export const BreakoutAlertWidget = ({ alert }: { alert: BreakoutAlert }) => {
    const isBullish = alert.action === 'BUY' || alert.action === 'ACCUMULATE';
    const isBearish =
        alert.action === 'SELL / AVOID' || alert.action === 'TAKE PROFIT';

    return (
        <div
            className={`h-full rounded-xl border p-6 border-l-4 shadow-sm ${
                isBullish
                    ? 'border-l-emerald-500 bg-emerald-50/20 border-emerald-100 dark:bg-emerald-900/10 dark:border-emerald-900/50'
                    : isBearish
                      ? 'border-l-red-500 bg-red-50/20 border-red-100 dark:bg-red-900/10 dark:border-red-900/50'
                      : 'border-l-slate-400 bg-white border-slate-200 dark:bg-slate-800 dark:border-slate-700'
            }`}
        >
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                        Quantitative Breakout Setup
                    </h2>
                    <p className="text-sm font-medium mt-1 text-slate-600 dark:text-slate-400">
                        Signal:{' '}
                        <span
                            className={
                                isBullish
                                    ? 'text-emerald-600 dark:text-emerald-400 font-bold'
                                    : isBearish
                                      ? 'text-red-600 dark:text-red-400 font-bold'
                                      : 'text-slate-600 dark:text-slate-400 font-bold'
                            }
                        >
                            {alert.signal}
                        </span>
                    </p>
                </div>
                <div
                    className={`px-3 py-1 rounded text-xs font-bold ${
                        isBullish
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : isBearish
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                              : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                    }`}
                >
                    {alert.action}
                </div>
            </div>

            <p className="text-sm text-slate-700 dark:text-slate-300 mb-6 italic border-l-2 border-slate-300 dark:border-slate-600 pl-3">
                &ldquo;{alert.reasoning}&rdquo;
            </p>

            {isBullish && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white dark:bg-slate-800 p-3 rounded-lg border dark:border-slate-700 shadow-sm">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Entry Target
                        </p>
                        <p className="font-mono font-bold dark:text-white">
                            ${fmtUsd(alert.buy_price)}
                        </p>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg border border-emerald-100 dark:border-emerald-800">
                        <p className="text-xs text-emerald-700 dark:text-emerald-400">
                            Take Profit
                        </p>
                        <p className="font-mono font-bold text-emerald-700 dark:text-emerald-300">
                            ${fmtUsd(alert.sell_target)}
                        </p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-100 dark:border-red-800">
                        <p className="text-xs text-red-700 dark:text-red-400">
                            Stop Loss
                        </p>
                        <p className="font-mono font-bold text-red-700 dark:text-red-300">
                            ${fmtUsd(alert.stop_loss)}
                        </p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
                        <p className="text-xs text-blue-700 dark:text-blue-400">
                            Exp. Return
                        </p>
                        <p className="font-mono font-bold text-blue-700 dark:text-blue-300">
                            +{alert.expected_return_pct?.toFixed(2)}%
                        </p>
                        <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-1">
                            {alert.holding_period}
                        </p>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-4 border-t dark:border-slate-700 pt-4 text-xs text-slate-500 dark:text-slate-400">
                <span>
                    RSI(14):{' '}
                    <strong className="text-slate-700 dark:text-slate-200">
                        {alert.rsi_14?.toFixed(1) ?? 'N/A'}
                    </strong>
                </span>
                <span>
                    EMA(20):{' '}
                    <strong className="text-slate-700 dark:text-slate-200">
                        ${fmtUsd(alert.ema_20)}
                    </strong>
                </span>
                <span>
                    SMA(50):{' '}
                    <strong className="text-slate-700 dark:text-slate-200">
                        ${fmtUsd(alert.sma_50)}
                    </strong>
                </span>
            </div>
        </div>
    );
};

export default BreakoutAlertWidget;
